import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Carousel, Spinner, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { UPLOAD_BASE_URL, BASE_URL } from '../../services/api';
import QuickStatsBar from '../../components/QuickStatsBar';
import 'animate.css/animate.min.css';

const Home = () => {
  const [homeData, setHomeData] = useState(null);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSlides, setLoadingSlides] = useState(true);
  const [error, setError] = useState('');
  const [slidesError, setSlidesError] = useState('');

  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState('');
  const [hasShownError, setHasShownError] = useState(false);

  const API_BASE_URL = BASE_URL;

  const fetchHomeData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/home.php`, {
        timeout: 10000
      });
      
      if (response.data.success) {
        setHomeData(response.data.data);
      } else {
        setError('Failed to load home page data');
        showErrorModalHandler('The server encountered an error while processing your request. Please try again later.', 'server');
      }
    } catch (err) {
      console.error('API Error:', err);
      
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        showErrorModalHandler('Unable to connect to the server. Please check your internet connection.', 'internet');
      } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        showErrorModalHandler('The server is taking too long to respond. Please check your connection or try again later.', 'server');
      } else if (err.response) {
        showErrorModalHandler(`Server error: ${err.response.status}. Please try again later.`, 'server');
      } else {
        showErrorModalHandler('An unexpected error occurred. Please try again.', 'server');
      }
      
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const fetchSlides = useCallback(async () => {
    try {
      setLoadingSlides(true);
      setSlidesError('');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${API_BASE_URL}/slider.php?action=getActive`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setSlides(result.data || []);
      } else {
        setSlidesError(result.message || 'Failed to load slides');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setSlidesError('Unable to load slides');
    } finally {
      setLoadingSlides(false);
    }
  }, [API_BASE_URL]);

  const showErrorModalHandler = useCallback((message, type) => {
    if (!hasShownError) {
      setErrorMessage(message);
      setErrorType(type);
      setShowErrorModal(true);
      setHasShownError(true);
    }
  }, [hasShownError]);

  useEffect(() => {
    fetchHomeData();
    fetchSlides();
  }, [fetchHomeData, fetchSlides]);

  const getFileType = (file) => {
    if (!file) return '';
    const ext = file.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'other';
  };

  const retryConnection = () => {
    setShowErrorModal(false);
    setErrorMessage('');
    setErrorType('');
    setHasShownError(false);
    
    if (!homeData) {
      setLoading(true);
      fetchHomeData();
    }
    
    if (slides.length === 0) {
      setLoadingSlides(true);
      fetchSlides();
    }
  };

  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading && !homeData && !slides.length && !slidesError) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading home page...</p>
      </Container>
    );
  }

  const renderSlider = () => {
    if (loadingSlides && slides.length === 0) {
      return (
        <div style={{ height: '85vh', minHeight: '600px' }} className="d-flex align-items-center justify-content-center bg-light">
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading slides...</p>
          </div>
        </div>
      );
    }

    if (slides.length > 0) {
      return (
        <Carousel fade controls indicators interval={4000}>
          {slides.map((slide) => (
            <Carousel.Item key={slide.id}>
              <div 
                className="carousel-slide"
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${slide.image_url.startsWith('http') ? slide.image_url : `${UPLOAD_BASE_URL}/${slide.image_url}`})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: '85vh',
                  minHeight: '600px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Container>
                  <Row className="align-items-center">
                    <Col lg={8} className="text-white">
                      <h1 className="display-3 fw-bold mb-4 animate__animated animate__fadeInUp">
                        {slide.title}
                      </h1>
                      <p className="lead mb-4 fs-4 animate__animated animate__fadeInUp animate__delay-1s">
                        {slide.subtitle}
                      </p>
                      {slide.button_text && slide.button_text.trim() !== '' && (
                        <Button 
                          as={Link} 
                          to={slide.button_link || '/'}
                          variant="warning" 
                          size="lg" 
                          className="px-5 py-3 animate__animated animate__fadeInUp animate__delay-2s"
                        >
                          <i className="fas fa-arrow-right me-2"></i>
                          {slide.button_text}
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Container>
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      );
    }

    if (homeData?.slider && homeData.slider.length > 0) {
      return (
        <Carousel fade controls indicators interval={4000}>
          {homeData.slider?.map((slide, index) => (
            <Carousel.Item key={slide.id || index}>
              <div 
                className="carousel-slide"
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${slide.image_url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: '85vh',
                  minHeight: '600px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Container>
                  <Row className="align-items-center">
                    <Col lg={8} className="text-white">
                      <h1 className="display-3 fw-bold mb-4 animate__animated animate__fadeInUp">
                        {slide.title}
                      </h1>
                      <p className="lead mb-4 fs-4 animate__animated animate__fadeInUp animate__delay-1s">
                        {slide.subtitle}
                      </p>
                      {slide.button_text && slide.button_text.trim() !== '' && (
                        <Button 
                          as={Link} 
                          to={slide.button_link || '/'}
                          variant="warning" 
                          size="lg" 
                          className="px-5 py-3 animate__animated animate__fadeInUp animate__delay-2s"
                        >
                          <i className="fas fa-arrow-right me-2"></i>
                          {slide.button_text}
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Container>
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      );
    }

    return (
      <div style={{ height: '85vh', minHeight: '600px' }} className="d-flex align-items-center justify-content-center bg-dark text-white">
        <div className="text-center">
          <i className="fas fa-images fa-4x mb-4 text-muted"></i>
          <h3>Welcome to Our Website</h3>
          <p className="lead">Excellence in Service Since 1998</p>
          <Button as={Link} to="/about" variant="primary" size="lg">
            Learn More About Us
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        size="md"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className={errorType === 'internet' ? 'bg-warning' : 'bg-danger text-white'}>
          <Modal.Title>
            <i className={`fas fa-${errorType === 'internet' ? 'wifi-slash' : 'server'} me-2`}></i>
            {errorType === 'internet' ? 'Internet Connection Error' : 'Server Error'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <i className={`fas fa-${errorType === 'internet' ? 'wifi-slash' : 'server'} fa-3x ${errorType === 'internet' ? 'text-warning' : 'text-danger'} mb-3`}></i>
            <p className="lead">{errorMessage}</p>
          </div>
          <div className="d-flex justify-content-between">
            <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
              Continue Anyway
            </Button>
            <Button 
              variant={errorType === 'internet' ? 'warning' : 'primary'} 
              onClick={retryConnection}
            >
              <i className="fas fa-redo me-2"></i>
              Try Again
            </Button>
          </div>
          {errorType === 'internet' && (
            <div className="mt-3 text-muted small">
              <i className="fas fa-info-circle me-1"></i>
              Some features may be limited without internet connection.
            </div>
          )}
        </Modal.Body>
      </Modal>

      <section className="hero-carousel" style={{ marginTop: '-140px', paddingTop: '140px' }}>
        {renderSlider()}
      </section>

      {/* Dynamic Quick Stats Bar */}
      <QuickStatsBar 
        stats={homeData?.quickStats} 
        loading={loading} 
        error={error}
        className="py-4"
      />

      {/* Quick Links Section */}
      {homeData?.quickLinks && homeData.quickLinks.length > 0 && (
        <section className="py-5 bg-light">
          <Container>
            <div className="text-center mb-4">
              <h2 className="fw-bold text-primary">Quick Links</h2>
              <p className="text-muted">Access important information quickly</p>
            </div>
            <Row className="g-4">
              {homeData.quickLinks.map((link) => (
                <Col lg={3} md={6} key={link.id}>
                  <Card as={Link} to={link.link} 
                        className="text-center border-0 shadow-sm h-100 text-decoration-none text-dark quick-link-card">
                    <Card.Body className="py-4 px-3">
                      <div className="quick-link-icon mb-3">
                        <i className={`${link.icon || 'fas fa-link'} fa-3x text-primary`}></i>
                      </div>
                      <Card.Title className="mb-0 fw-bold">{link.title}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Features Section */}
      {homeData?.features && homeData.features.length > 0 && (
        <section className="py-5">
          <Container>
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold text-primary mb-3">Why Choose Us</h2>
              <p className="lead text-muted">"We provide an environment that fosters excellence, innovation, and sustainable growth across all fields."</p>
            </div>
            <Row>
              {homeData.features.map((feature) => (
                <Col lg={4} md={6} className="mb-4" key={feature.id}>
                  <Card className="h-100 border-0 shadow-sm feature-card">
                    <Card.Body className="p-4 text-center">
                      <div className="feature-icon mb-4">
                        <div className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center" 
                             style={{ width: '80px', height: '80px' }}>
                          <i className={`${feature.icon || 'fas fa-check'} fa-2x text-primary`}></i>
                        </div>
                      </div>
                      <Card.Title className="mb-3 fw-bold">{feature.title}</Card.Title>
                      <Card.Text className="text-muted">{feature.description}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* About Section */}
      {homeData?.about && (
        <section className="py-5 bg-dark text-white">
          <Container>
            <Row className="align-items-center">
              <Col lg={6} className="mb-5 mb-lg-0">
                <h2 className="display-6 fw-bold mb-4">{homeData.about.section_title}</h2>
                <p className="lead mb-4">{homeData.about.content}</p>
                <Button 
                  as={Link} 
                  to={homeData.about.button_link || '/about'} 
                  variant="outline-light" 
                  size="lg" 
                  className="px-4"
                >
                  {homeData.about.button_text}
                </Button>
              </Col>
              <Col lg={6}>
                <div className="position-relative">
                  {homeData?.about?.image_url?.trim() ? (
                    <img 
                      src={`${UPLOAD_BASE_URL}${homeData.about.image_url}`}
                      alt="College Campus" 
                      className="img-fluid rounded shadow-lg"
                    />
                  ) : (
                    <div className="bg-secondary rounded shadow-lg d-flex align-items-center justify-content-center" 
                         style={{ height: '400px' }}>
                      <i className="fas fa-building fa-4x text-light opacity-50"></i>
                    </div>
                  )}
                  <div className="position-absolute bottom-0 start-0 bg-warning text-dark p-3 rounded-end" 
                       style={{ transform: 'translateY(50%)' }}>
                    <h5 className="mb-0">Our Logo</h5>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      )}

      {/* Announcements Section */}
      {homeData?.announcements && homeData.announcements.length > 0 && (
        <section className="py-5">
          <Container>
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold text-primary mb-3">Latest Announcements</h2>
              <p className="lead text-muted">Stay updated with eda Builders & Promoters news and important information</p>
            </div>
            <Row>
              {homeData.announcements.map((announcement) => (
                <Col lg={4} md={6} className="mb-4" key={announcement.id}>
                  <Card className="h-100 border-0 shadow-sm announcement-card">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-start mb-3">
                        <div
                          className={`bg-${announcement.icon_color || 'danger'} rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0`}
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className={`${announcement.icon || 'fas fa-bullhorn'} text-white fa-lg`}></i>
                        </div>
                        <div>
                          <h5 className={`mb-2 text-${announcement.icon_color || 'danger'}`}>
                            {announcement.title}
                          </h5>
                          <small className="text-muted d-block mb-2">
                            <i className="fas fa-calendar-alt me-1"></i>
                            {formatDate(announcement.date)}
                          </small>
                        </div>
                      </div>

                      <Card.Text className="text-muted">
                        {announcement.content.length > 120
                          ? announcement.content.substring(0, 120) + '...'
                          : announcement.content}
                      </Card.Text>

                      {announcement.attachment && (
                        <div className="mb-2">
                          <i className="fas fa-paperclip text-primary me-1"></i>
                          <small className="text-primary">Attachment Available</small>
                        </div>
                      )}

                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          setSelectedAnnouncement(announcement);
                          setShowAnnouncementModal(true);
                        }}
                      >
                        Read More <i className="fas fa-arrow-right ms-1"></i>
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Announcement Modal */}
      <Modal
        show={showAnnouncementModal}
        onHide={() => setShowAnnouncementModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedAnnouncement?.title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-muted">
            <i className="fas fa-calendar-alt me-2"></i>
            {selectedAnnouncement && formatDate(selectedAnnouncement.date)}
          </p>

          <p>{selectedAnnouncement?.content}</p>

          {selectedAnnouncement?.attachment && (
            <>
              <hr />
              <h6 className="fw-bold mb-3">Attachment</h6>

              {getFileType(selectedAnnouncement.attachment) === 'image' && (
                <img
                  src={`${UPLOAD_BASE_URL}${selectedAnnouncement.attachment}`}
                  alt="Attachment"
                  className="img-fluid rounded"
                />
              )}

              {getFileType(selectedAnnouncement.attachment) === 'pdf' && (
                <iframe
                  src={`${UPLOAD_BASE_URL}${selectedAnnouncement.attachment}`}
                  width="100%"
                  height="400"
                  title="PDF Preview"
                />
              )}

              <div className="mt-3">
                <a
                  href={`${UPLOAD_BASE_URL}${selectedAnnouncement.attachment}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary"
                >
                  <i className="fas fa-download me-2"></i>
                  Download Attachment
                </a>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Team Section */}
      {homeData?.team && homeData.team.length > 0 && (
        <section className="py-5 bg-light">
          <Container>
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold text-primary mb-3">Our Leadership Team</h2>
              <p className="lead text-muted">Meet our dedicated team of educators and administrators</p>
            </div>
            <Row>
              {homeData.team.map((member) => (
                <Col lg={4} md={6} className="mb-4" key={member.id}>
                  <Card className="h-100 border-0 shadow-sm text-center team-card">
                    <Card.Body className="p-4">
                      <div className="mb-3">
                        <img 
                          src={
                            member.image_url
                              ? `${UPLOAD_BASE_URL}${member.image_url}`
                              : 'https://cdn-icons-png.flaticon.com/512/9187/9187604.png'
                          }
                          alt={member.name}
                          className="rounded-circle img-fluid"
                          style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                        />
                      </div>
                      <Card.Title className="mb-1 fw-bold">{member.name}</Card.Title>
                      <Card.Subtitle className="mb-3 text-primary">{member.designation}</Card.Subtitle>
                      {member.department && (
                        <p className="text-muted small mb-3">{member.department}</p>
                      )}
                      <Card.Text className="text-muted">{member.bio}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Testimonials Section */}
      {homeData?.testimonials && homeData.testimonials.length > 0 && (
        <section className="py-5">
          <Container>
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold text-primary mb-3">What People Say</h2>
              <p className="lead text-muted">Hear from our Clients</p>
            </div>
            <Row>
              {homeData.testimonials.map((testimonial) => (
                <Col lg={4} md={6} className="mb-4" key={testimonial.id}>
                  <Card className="h-100 border-0 shadow-sm testimonial-card">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <img 
                          src={
                            testimonial.image_url
                              ? `${UPLOAD_BASE_URL}${testimonial.image_url}`
                              : 'https://cdn-icons-png.flaticon.com/512/9187/9187604.png'
                          }
                          alt={testimonial.name}
                          className="rounded-circle me-3"
                          style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                        />
                        <div>
                          <h6 className="mb-0 fw-bold">{testimonial.name}</h6>
                          <small className="text-muted">{testimonial.designation}</small>
                        </div>
                      </div>
                      <div className="mb-3">
                        {[...Array(5)].map((_, i) => (
                          <i 
                            key={i} 
                            className={`fas fa-star ${i < (testimonial.rating || 5) ? 'text-warning' : 'text-muted'}`}
                          ></i>
                        ))}
                      </div>
                      <Card.Text className="text-muted">
                        "{testimonial.content}"
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Call to Action Section */}
      {homeData?.callToAction && (
        <section className={`py-5 bg-${homeData.callToAction.background_color || 'primary'} text-white`}>
          <Container className="text-center">
            <h2 className="display-5 fw-bold mb-4">{homeData.callToAction.title}</h2>
            <p className="lead mb-4 opacity-75 fs-5">
              {homeData.callToAction.description}
            </p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              {homeData.callToAction.buttons?.map((button, index) => (
                <Button 
                  key={index} 
                  as={Link} 
                  to={button.link || '/'} 
                  variant={button.variant || 'light'} 
                  size="lg" 
                  className="px-5 py-3 fw-bold"
                >
                  <i className={`${button.icon || 'fas fa-arrow-right'} me-2`}></i> 
                  {button.text}
                </Button>
              ))}
            </div>
          </Container>
        </section>
      )}

      <style jsx="true">{`
        /* Fix header overlap - Updated for 140px navbar height */
        .hero-carousel {
          margin-top: -140px;
          padding-top: 140px;
        }
        
        /* Carousel custom styles */
        .carousel-slide {
          position: relative;
          animation: fadeIn 1s ease-in;
        }
        
        .carousel-control-prev,
        .carousel-control-next {
          width: 5%;
        }
        
        .carousel-indicators button {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          margin: 0 5px;
        }
        
        /* Quick link cards */
        .quick-link-card {
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }
        
        .quick-link-card:hover {
          transform: translateY(-10px);
          border-color: var(--bs-primary);
          box-shadow: 0 10px 20px rgba(0,0,0,0.15) !important;
        }
        
        .quick-link-icon {
          transition: transform 0.3s ease;
        }
        
        .quick-link-card:hover .quick-link-icon {
          transform: scale(1.1);
        }
        
        /* Feature cards */
        .feature-card {
          transition: all 0.3s ease;
        }
        
        .feature-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
        }
        
        .feature-card:hover .feature-icon {
          transform: scale(1.1);
        }
        
        .feature-icon {
          transition: transform 0.3s ease;
        }
        
        /* Announcement cards */
        .announcement-card {
          transition: all 0.3s ease;
        }
        
        .announcement-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        
        /* Team cards */
        .team-card {
          transition: all 0.3s ease;
        }
        
        .team-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
        }
        
        /* Testimonial cards */
        .testimonial-card {
          transition: all 0.3s ease;
        }
        
        .testimonial-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .carousel-slide {
            height: 60vh;
            min-height: 400px;
          }
          
          .hero-carousel h1 {
            font-size: 2.5rem;
          }
          
          .hero-carousel .lead {
            font-size: 1.2rem;
          }
          
          /* Adjust for mobile navbar */
          .hero-carousel {
            margin-top: -90px;
            padding-top: 90px;
          }
        }
        
        @media (max-width: 576px) {
          .carousel-slide {
            height: 50vh;
            min-height: 350px;
          }
          
          .hero-carousel h1 {
            font-size: 2rem;
          }
          
          .hero-carousel .lead {
            font-size: 1rem;
          }
        }
        
        /* Animation keyframes */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        /* Ensure proper spacing for the new navbar */
        body {
          padding-top: 0px !important;
        }
        
        @media (max-width: 991.98px) {
          body {
            padding-top: 0px !important;
          }
          
          .hero-carousel {
            margin-top: -90px;
            padding-top: 90px;
          }
        }
      `}</style>
    </>
  );
};

export default Home;